import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { getGroups, addGroup, deleteGroup, updateGroup } from '../../actions/groupActions';
import { getPrograms, addProgram, deleteProgram, updateProgram } from '../../actions/programActions';
import { getPaths } from '../../actions/pathActions';
import { getQuestions } from '../../actions/questionActions';
import { getSubmissionForms } from '../../actions/submissionFormActions';
import { getTags } from '../../actions/tagActions';
import { getUsers } from '../../actions/userActions';
import { getTermsConditions, getAvailablePrograms, addTermsCondition, deleteTermsCondition, updateTermsCondition } from '../../actions/termsConditionsActions';
import AdminDashboard from './AdminDashboard';
import InvitationsTab from './InvitationsTab';
import ManageUsersTab from './ManageUsersTab';
import PerformanceTab from './PerformanceTab';
import ImportExportDataTab from './ImportExportDataTab';
import LogsTab from '../LogsTab';
import GroupForm from './GroupForm';
import ProgramForm from './ProgramForm';
import TermsConditionForm from '../content-management/TermsConditionForm';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import { X } from 'lucide-react';
import { isAdminRole } from '../../utils/roleHelpers';
import MondaySyncTab from './MondaySyncTab';

const TABS = ['Admin Settings', 'Performance', 'Logs', 'Invitations', 'Manage Users', 'Programs', 'Terms & Conditions', 'Groups', 'Import/Export Data', 'Monday Sync'];

const SettingsContainer = ({ 
  user,
  group: { groups, loading: groupsLoading },
  program: { programs, loading: programsLoading },
  path: { paths, loading: pathsLoading },
  question: { questions, loading: questionsLoading },
  submissionForm: { submissionForms, loading: submissionFormsLoading },
  userState: { users, loading: usersLoading },
  termsConditions: { termsConditions, availablePrograms, loading: termsConditionsLoading },
  tag: { tags, loading: tagsLoading },
  getGroups,
  addGroup,
  deleteGroup,
  updateGroup,
  getPrograms,
  addProgram,
  deleteProgram,
  updateProgram,
  getPaths,
  getQuestions,
  getSubmissionForms,
  getUsers,
  getTags,
  getTermsConditions,
  getAvailablePrograms,
  addTermsCondition,
  deleteTermsCondition,
  updateTermsCondition,
  initialTab
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupFormMode, setGroupFormMode] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [programFormMode, setProgramFormMode] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [showTermsConditionForm, setShowTermsConditionForm] = useState(false);
  const [termsConditionFormMode, setTermsConditionFormMode] = useState(null);
  const [selectedTermsCondition, setSelectedTermsCondition] = useState(null);
  const [termsConditionToDelete, setTermsConditionToDelete] = useState(null);

  // Check if user is admin
  const isAdmin = isAdminRole(user?.role);
  const isPlatformAdmin = user?.role === 'platform_admin';

  // Filter tabs — Performance and Logs only visible to platform_admin
  const visibleTabs = TABS.filter(tab => {
    if (tab === 'Performance' || tab === 'Logs') return isPlatformAdmin;
    return true;
  });

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (isAdmin) {
      getGroups();
      getPrograms();
      getPaths();
      getQuestions();
      getSubmissionForms();
      getUsers();
      getTags();
      getTermsConditions();
      getAvailablePrograms();
    }
  }, [isAdmin, getGroups, getPrograms, getPaths, getQuestions, getSubmissionForms, getUsers, getTags, getTermsConditions, getAvailablePrograms]);

  // Group handlers
  const handleAddGroup = () => {
    setGroupFormMode('new');
    setSelectedGroup(null);
    setShowGroupForm(true);
  };
  const handleViewGroup = (group) => {
    setGroupFormMode('view');
    setSelectedGroup(group);
    setShowGroupForm(true);
  };
  const handleEditGroup = (group) => {
    setGroupFormMode('edit');
    setSelectedGroup(group);
    setShowGroupForm(true);
  };
  const handleGroupFormClose = () => {
    setShowGroupForm(false);
    setSelectedGroup(null);
    setGroupFormMode(null);
  };
  const handleGroupFormSubmit = async (data) => {
    try {
      if (groupFormMode === 'edit') {
        await updateGroup(selectedGroup._id, data);
      } else if (groupFormMode === 'new') {
        await addGroup(data);
      }
      handleGroupFormClose();
    } catch (error) {
      // Error will be handled by the action and shown in GroupForm
      throw error;
    }
  };

  // Program handlers
  const handleAddProgram = () => {
    setProgramFormMode('new');
    setSelectedProgram(null);
    setShowProgramForm(true);
  };
  const handleViewProgram = (program) => {
    setProgramFormMode('view');
    setSelectedProgram(program);
    setShowProgramForm(true);
  };
  const handleEditProgram = (program) => {
    setProgramFormMode('edit');
    setSelectedProgram(program);
    setShowProgramForm(true);
  };
  const handleProgramFormClose = () => {
    setShowProgramForm(false);
    setSelectedProgram(null);
    setProgramFormMode(null);
  };
  const handleProgramFormSubmit = (data) => {
    if (programFormMode === 'edit') {
      updateProgram(selectedProgram._id, data);
    } else if (programFormMode === 'new') {
      addProgram(data);
    }
    handleProgramFormClose();
  };

  // Terms & Conditions handlers
  const handleAddTermsCondition = () => {
    getAvailablePrograms(); // Refresh available programs
    setTermsConditionFormMode('new');
    setSelectedTermsCondition(null);
    setShowTermsConditionForm(true);
  };
  const handleViewTermsCondition = (termsCondition) => {
    setTermsConditionFormMode('view');
    setSelectedTermsCondition(termsCondition);
    setShowTermsConditionForm(true);
  };
  const handleEditTermsCondition = (termsCondition) => {
    getAvailablePrograms(); // Refresh available programs
    setTermsConditionFormMode('edit');
    setSelectedTermsCondition(termsCondition);
    setShowTermsConditionForm(true);
  };
  const handleTermsConditionFormClose = () => {
    setShowTermsConditionForm(false);
    setSelectedTermsCondition(null);
    setTermsConditionFormMode(null);
  };

  // Get appropriate programs list based on mode
  const getAvailableProgramsForMode = () => {
    if (!availablePrograms) return [];
    
    if (termsConditionFormMode === 'new') {
      // New mode: only truly available programs
      return availablePrograms.availablePrograms || [];
    } else if (termsConditionFormMode === 'edit' && selectedTermsCondition) {
      // Edit mode: available programs + currently assigned program (if any)
      const available = availablePrograms.availablePrograms || [];
      const currentProgramId = selectedTermsCondition.program?._id;
      
      if (currentProgramId) {
        // Find the currently assigned program from allPrograms
        const currentProgram = (availablePrograms.allPrograms || []).find(
          p => p._id === currentProgramId
        );
        
        // If current program exists and is not already in available list, add it
        if (currentProgram && !available.find(p => p._id === currentProgramId)) {
          return [currentProgram, ...available];
        }
      }
      
      return available;
    } else {
      // View mode: all programs (to show current assignment)
      return availablePrograms.allPrograms || [];
    }
  };
  const handleTermsConditionFormSubmit = (data) => {
    if (termsConditionFormMode === 'edit') {
      updateTermsCondition(selectedTermsCondition._id, data);
    } else if (termsConditionFormMode === 'new') {
      addTermsCondition(data);
    }
    // Refresh available programs after submission
    setTimeout(() => {
      getAvailablePrograms();
    }, 500);
    handleTermsConditionFormClose();
  };

  const handleDeleteClick = (item) => {
    if (activeTab === 'Programs') {
      setProgramToDelete(item);
    } else if (activeTab === 'Groups') {
      setGroupToDelete(item);
    } else if (activeTab === 'Terms & Conditions') {
      setTermsConditionToDelete(item);
    }
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (activeTab === 'Programs' && programToDelete) {
      deleteProgram(programToDelete._id);
      setProgramToDelete(null);
    } else if (activeTab === 'Groups' && groupToDelete) {
      deleteGroup(groupToDelete._id);
      setGroupToDelete(null);
    } else if (activeTab === 'Terms & Conditions' && termsConditionToDelete) {
      deleteTermsCondition(termsConditionToDelete._id);
      setTermsConditionToDelete(null);
      // Refresh available programs after deletion
      setTimeout(() => {
        getAvailablePrograms();
      }, 500);
    }
    setShowDeleteConfirmation(false);
  };

  const handleCancelDelete = () => {
    setProgramToDelete(null);
    setGroupToDelete(null);
    setTermsConditionToDelete(null);
    setShowDeleteConfirmation(false);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Admin Settings':
        return <AdminDashboard />;
      case 'Performance':
        return <PerformanceTab />;
      case 'Logs':
        return <LogsTab />;
      case 'Invitations':
        return <InvitationsTab />;
      case 'Manage Users':
        return <ManageUsersTab />;
      case 'Programs':
        return (
          <>
            <ProgramForm
              mode={showProgramForm ? programFormMode : 'list'}
              program={selectedProgram}
              programs={programs}
              paths={paths || []}
              groups={groups || []}
              questions={questions || []}
              submissionForms={submissionForms || []}
              tags={tags || []}
              onEdit={(program) => {
                setProgramFormMode(program ? 'edit' : 'new');
                setSelectedProgram(program);
                setShowProgramForm(true);
              }}
              onDelete={handleDeleteClick}
              onClose={handleProgramFormClose}
              onSubmit={handleProgramFormSubmit}
            />
          </>
        );
      case 'Terms & Conditions':
        return (
          <>
            <TermsConditionForm
              mode={showTermsConditionForm ? termsConditionFormMode : 'list'}
              termsCondition={selectedTermsCondition}
              termsConditions={termsConditions || []}
              availablePrograms={getAvailableProgramsForMode()}
              onView={handleViewTermsCondition}
              onEdit={(termsCondition) => {
                setTermsConditionFormMode(termsCondition ? 'edit' : 'new');
                setSelectedTermsCondition(termsCondition);
                setShowTermsConditionForm(true);
              }}
              onDelete={handleDeleteClick}
              onClose={handleTermsConditionFormClose}
              onSubmit={handleTermsConditionFormSubmit}
            />
          </>
        );
      case 'Groups':
        return (
          <>
            <GroupForm
              mode={showGroupForm ? groupFormMode : 'list'}
              group={selectedGroup}
              groups={groups}
              users={users || []}
              programs={programs || []}
              onEdit={(group) => {
                setGroupFormMode(group ? 'edit' : 'new');
                setSelectedGroup(group);
                setShowGroupForm(true);
              }}
              onDelete={handleDeleteClick}
              onClose={handleGroupFormClose}
              onSubmit={handleGroupFormSubmit}
            />
          </>
        );
      case 'Import/Export Data':
        return <ImportExportDataTab />;
      case 'Monday Sync':
        return <MondaySyncTab />;
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="p-4 sm:p-6">
        {renderTabContent()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-[#054581]">
                Confirm Delete
              </h3>
              <button
                onClick={handleCancelDelete}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this {
                activeTab === 'Programs' ? 'program' : 
                activeTab === 'Terms & Conditions' ? 'terms & conditions' :
                'group'
              }? This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="w-full sm:w-auto px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="w-full sm:w-auto px-4 py-2 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.auth.user,
  group: state.group,
  program: state.program,
  path: state.path,
  question: state.question,
  submissionForm: state.submissionForm,
  userState: state.user,
  termsConditions: state.termsConditions,
  tag: state.tag
});

export default connect(mapStateToProps, {
  getGroups,
  addGroup,
  deleteGroup,
  updateGroup,
  getPrograms,
  addProgram,
  deleteProgram,
  updateProgram,
  getPaths,
  getQuestions,
  getSubmissionForms,
  getUsers,
  getTags,
  getTermsConditions,
  getAvailablePrograms,
  addTermsCondition,
  deleteTermsCondition,
  updateTermsCondition
})(SettingsContainer);
